import React from 'react';
import styled, { css } from 'styled-components';
import {
  brandColours,
  brandFonts,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  standardColours,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Button, Container, Heading, Link, Svg } from './ui';
import longArrowIcon from '../images/long-arrow-icon.inline.svg';

const StyledPlansTable = styled.div`
  ${maxBreakpointQuery.mlarge`
    display: none;
  `}
`;

const StyledTable = styled.table`
  display: table;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
`;

const StyledCaption = styled.caption`
  span {
    ${visuallyHidden()}
  }
`;

const StyledColumn = styled.col`
  ${({ isHighlighted }) => {
    if (isHighlighted) {
      return css`
        background-color: ${standardColours.white};
      `;
    }
  }}
`;

const StyledOverline = styled.p`
  ${fontSize(18)}
  margin-bottom: 15px;
  text-align: left;
  font-weight: ${fontWeights.regular};
`;

const StyledHeading = styled(Heading)`
  text-align: left;
  font-weight: ${fontWeights.medium};
`;

const StyledHeader = styled.th`
  width: 430px;
`;

const StyledPlanHeader = styled.th`
  position: relative;
  padding: 24px;
  border-top-right-radius: 40px;
  border-top-left-radius: 40px;
  vertical-align: top;
`;

const StyledPopular = styled.div`
  position: absolute;
  display: flex;
  top: -40px;
  left: 50%;
  transform: translateX(0%);
`;

const StyledPopularIcon = styled(Svg)`
  margin-top: 20px;
  margin-right: 10px;
`;

const StyledPopularText = styled.p`
  width: 55px;
  font-family: ${brandFonts.secondary};
`;

const StyledName = styled.h3`
  ${fontSize(25)}
`;

const StyledPriceHeading = styled.p`
  margin-top: 10px;
  font-weight: ${fontWeights.regular};
`;

const StyledPriceSubheading = styled.p`
  margin-top: 15px;
  font-weight: ${fontWeights.regular};
`;

const StyledTableBody = styled.tbody`
  tr {
    &:last-child {
      th,
      td {
        border-bottom: 0;
      }
    }
  }
`;

const StyledFeatureGroupHeader = styled.th`
  padding-top: 20px;
  padding-bottom: 5px;
  ${fontSize(20)}
  font-weight: ${fontWeights.semibold};
  text-align: left;

  span {
    ${({ hide }) => {
      if (hide) {
        return css`
          ${visuallyHidden()}
        `;
      }
    }}
  }
`;

const StyledFeatureHeader = styled.th`
  padding: 15px 0px;
  font-weight: ${fontWeights.regular};
  text-align: left;
  border-bottom: 1px solid #d5d5d9;

  a {
    position: relative;
    border-bottom: 1px solid transparent;
    transition: ${standardTransition('color')},
      ${standardTransition('border-color')};

    &:hover {
      color: ${brandColours.secondary};
      border-bottom-color: ${brandColours.secondary};
    }
  }
`;

const StyledFeatureData = styled.td`
  text-align: center;
  border-bottom: 1px solid #d5d5d9;
`;

const StyledTick = styled.span`
  position: relative;
  width: 25px;
  height: 25px;
  display: inline-block;
  background-color: #8dd400;
  border-radius: 50%;

  &:after {
    content: '';
    position: absolute;
    left: 10px;
    top: 6px;
    width: 6px;
    height: 11px;
    border: solid ${standardColours.white};
    border-width: 0 2px 2px 0;
    -webkit-transform: rotate(45deg);
    -ms-transform: rotate(45deg);
    transform: rotate(45deg);
  }
`;

const StyledTableFootData = styled.td`
  text-align: center;
  padding-bottom: 20px;

  &:nth-child(3) {
    border-bottom-right-radius: 40px;
    border-bottom-left-radius: 40px;
  }
`;

const StyledButton = styled(Button)`
  margin-top: 30px;
  margin-left: auto;
  margin-right: auto;
  ${fontSize(12)}
  padding: 12px 16px;

  ${minBreakpointQuery.large`
    padding: 18px 26px;
  `}

  ${minBreakpointQuery.xlarge`
    padding: 18px 34px;
  `}
`;

const StyledBottom = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;

const StyledVatText = styled.p`
  ${fontSize(12)};
`;

const PlansTable = ({
  overline,
  heading,
  plans,
  featureCategories,
  features,
  activePlan,
}) => (
  <StyledPlansTable>
    <Container>
      <StyledTable>
        <StyledCaption>
          <span>Pricing table</span>
        </StyledCaption>
        <colgroup>
          <col />
          {plans.map(({ name, isMostPopular }, id) => (
            <StyledColumn
              key={id}
              isHighlighted={activePlan ? activePlan === name : isMostPopular}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            <StyledHeader>
              {overline && <StyledOverline>{overline}</StyledOverline>}
              <StyledHeading>{heading}</StyledHeading>
            </StyledHeader>
            {plans.map(({ name, heading, subheading, isMostPopular }, id) => (
              <StyledPlanHeader key={id}>
                {isMostPopular && (
                  <StyledPopular>
                    <StyledPopularIcon image={longArrowIcon} />
                    <StyledPopularText>Most popular</StyledPopularText>
                  </StyledPopular>
                )}
                <StyledName>{name}</StyledName>
                <StyledPriceHeading
                  dangerouslySetInnerHTML={{
                    __html: heading.replace(/(<p>|<\/p>)/g, ''),
                  }}
                />
                {subheading && (
                  <StyledPriceSubheading
                    dangerouslySetInnerHTML={{
                      __html: subheading.replace(/(<p>|<\/p>)/g, ''),
                    }}
                  />
                )}
              </StyledPlanHeader>
            ))}
          </tr>
        </thead>
        {featureCategories.map(({ title }, id) => {
          const { nodes } = features.categories.find(
            ({ fieldValue }) => fieldValue === title,
          );

          return (
            <StyledTableBody key={id}>
              <tr>
                <StyledFeatureGroupHeader>{title}</StyledFeatureGroupHeader>
                {plans.map(({ name }, id) => (
                  <StyledFeatureGroupHeader key={id} hide={true}>
                    <span>{name}</span>
                  </StyledFeatureGroupHeader>
                ))}
              </tr>
              {nodes.map((feature, id) => {
                const {
                  title,
                  plansFeatureTitleOverride,
                  plans: featurePlans,
                } = feature;

                return (
                  <tr key={id}>
                    <StyledFeatureHeader>
                      <Link
                        to={feature}
                        title={`Read more about ${plansFeatureTitleOverride ? plansFeatureTitleOverride : title}`}
                      >
                        {plansFeatureTitleOverride
                          ? plansFeatureTitleOverride
                          : title}
                      </Link>
                    </StyledFeatureHeader>
                    {plans.map(({ name }, id) => {
                      let featureInPlan = false;

                      if (featurePlans.length > 0) {
                        const featurePlan = featurePlans.filter(plan => {
                          return plan.plan.name === name && plan.includedInPlan;
                        });

                        if (featurePlan.length > 0) {
                          // See if we have a text replacement
                          featureInPlan = featurePlan[0].textOverride
                            ? featurePlan[0].textOverride
                            : true;
                        }
                      }

                      return (
                        <StyledFeatureData key={id}>
                          {featureInPlan ? (
                            typeof featureInPlan === 'boolean' ? (
                              <StyledTick />
                            ) : (
                              featureInPlan
                            )
                          ) : (
                            <span>&#8212;</span>
                          )}
                        </StyledFeatureData>
                      );
                    })}
                  </tr>
                );
              })}
            </StyledTableBody>
          );
        })}
        <tfoot>
          <tr>
            <td />
            {plans.map(({ ctaLink }, id) => (
              <StyledTableFootData key={id}>
                <StyledButton to={ctaLink.pageUrl} alt={true}>
                  {ctaLink.text}
                </StyledButton>
              </StyledTableFootData>
            ))}
          </tr>
        </tfoot>
      </StyledTable>
      <StyledBottom>
        <StyledVatText>* All Prices Exclude VAT</StyledVatText>
      </StyledBottom>
    </Container>
  </StyledPlansTable>
);

export default PlansTable;
